import { CloudUpload, DatabaseBackup, Download, History } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button';
import { Panel } from '../../components/ui/panel';
import { Skeleton } from '../../components/ui/skeleton';
import { ApiError } from '../../lib/api';
import {
  useBackups,
  useCreateBackup,
  useRemoteBackup,
  useRestoreBackup,
  useUploadBackup,
} from './queries';
import { RemoteBackupForm } from './RemoteBackupForm';

function formatSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Full backups: data and covers in one file in the backups folder, written
 * nightly and on demand. A fresh install restores one from that folder.
 */
export function BackupPanel() {
  const { t } = useTranslation();
  const overview = useBackups();
  const create = useCreateBackup();
  const restore = useRestoreBackup();
  const remote = useRemoteBackup();
  const upload = useUploadBackup();
  const remoteReady = Boolean(remote.data?.enabled && remote.data.host && remote.data.hasPassword);
  const [note, setNote] = useState<{ text: string; warning?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function backupNow() {
    setError(null);
    setNote(null);
    try {
      const { backup: info, upload: uploaded } = await create.mutateAsync();
      const written = t('backup.written', { name: info.name, size: formatSize(info.size) });
      setNote(
        !uploaded
          ? { text: written }
          : uploaded.ok
            ? { text: `${written} ${t('backup.uploaded')}` }
            : { text: written, warning: t('backup.uploadFailed', { message: uploaded.message }) },
      );
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('form.saveFailed'));
    }
  }

  async function uploadNow(name: string) {
    setError(null);
    setNote(null);
    const result = await upload.mutateAsync(name);
    setNote(
      result.ok
        ? { text: result.message }
        : { text: name, warning: t('backup.uploadFailed', { message: result.message }) },
    );
  }

  async function restoreFrom(name: string, createdAt: string) {
    if (!window.confirm(t('backup.restoreConfirm', { date: formatDate(createdAt) }))) return;
    setError(null);
    setNote(null);
    try {
      const result = await restore.mutateAsync(name);
      setNote({
        text: t('backup.restored', {
          date: formatDate(result.createdAt),
          series: result.counts.series ?? 0,
          issues: result.counts.issues ?? 0,
          copies: result.counts.collection_items ?? 0,
        }),
        ...(result.credentialsKept ? {} : { warning: t('backup.credentialsDropped') }),
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('form.saveFailed'));
    }
  }

  const data = overview.data;

  return (
    <Panel className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="flex items-center gap-2 text-xl text-foreground">
            <DatabaseBackup className="size-5 text-accent" />
            {t('backup.title')}
          </h2>
          <p className="mt-1 text-sm text-muted">
            {data?.nightlyHour === null
              ? t('backup.nightlyOff')
              : t('backup.nightly', { hour: data?.nightlyHour ?? 3, keep: data?.keep ?? 14 })}
          </p>
        </div>
        <Button disabled={create.isPending} onClick={() => void backupNow()}>
          <DatabaseBackup className={create.isPending ? 'size-4 animate-pulse' : 'size-4'} />
          {create.isPending ? t('backup.writing') : t('backup.now')}
        </Button>
      </div>

      {overview.isPending ? (
        <Skeleton className="h-20" />
      ) : (data?.backups.length ?? 0) === 0 ? (
        <p className="rounded-lg border border-dashed p-4 text-sm text-muted">{t('backup.none')}</p>
      ) : (
        <ul className="flex flex-col divide-y rounded-lg border">
          {data?.backups.map((entry) => (
            <li key={entry.name} className="flex flex-wrap items-center gap-3 px-3 py-2">
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-foreground">{formatDate(entry.createdAt)}</span>
                <span className="block truncate text-xs text-muted">
                  {entry.name} · {formatSize(entry.size)}
                </span>
              </span>
              <a
                href={`/api/backups/${encodeURIComponent(entry.name)}`}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-surface-raised px-3 text-sm text-foreground hover:bg-border"
              >
                <Download className="size-4" />
                {t('backup.download')}
              </a>
              {remoteReady ? (
                <Button
                  size="sm"
                  variant="surface"
                  disabled={upload.isPending}
                  onClick={() => void uploadNow(entry.name)}
                >
                  <CloudUpload className="size-4" />
                  {t('backup.upload')}
                </Button>
              ) : null}
              {data.canRestore ? (
                <Button
                  size="sm"
                  variant="surface"
                  disabled={restore.isPending}
                  onClick={() => void restoreFrom(entry.name, entry.createdAt)}
                >
                  <History className="size-4" />
                  {t('backup.restore')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-muted">
        {data?.canRestore ? t('backup.restoreHintEmpty') : t('backup.restoreHint')}
      </p>

      {note ? (
        <div
          role="status"
          className="rounded-lg border bg-surface-raised p-3 text-sm text-foreground"
        >
          <p>{note.text}</p>
          {note.warning ? <p className="mt-1 text-danger">{note.warning}</p> : null}
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}

      <RemoteBackupForm />
    </Panel>
  );
}
